/**
 * Utility functions for generating page titles and descriptions
 * Used by client-side React components. See `PageMetadataService.java` for server-side code.
 */

// Default values (fallback for server-side rendering)
const DEFAULT_TITLE = "Decide Together";
const DEFAULT_DESCRIPTION = "Decide together about anything using ranked vote elections.";
const SITE_SUFFIX = " – Decide Together";
const DOMAIN = "decidetogether.net";

/**
 * Generate internationalized page title based on route and election data
 * @param {string} route - The current route pattern
 * @param {Object} electionData - Election data object with title, id, etc.
 * @param {Function} t - i18n translation function
 * @returns {string} Internationalized page title
 */
export function getPageTitleI18n(route, electionData = null, t = null) {
    // Fallback to non-i18n version if no translation function provided
    if (!t) {
        return getPageTitle(route, electionData);
    }

    // Handle loading state
    if (!electionData && needsElectionData(route)) {
        return getLoadingTitleI18n(route, t);
    }

    const electionTitle = electionData?.title || t('common:metadata.titles.loading.election');
    const siteSuffix = t('common:metadata.siteSuffix');

    switch (route) {
        case '/':
            return t('common:metadata.titles.home');
        
        case '/create-election':
            return t('common:metadata.titles.createElection') + siteSuffix;
        
        case '/ranked-elections':
            return t('common:metadata.titles.elections') + siteSuffix;
        
        case '/ranked-elections/:id':
            return t('common:metadata.titles.election', { title: electionTitle }) + siteSuffix;
        
        case '/ranked-elections/:id/create-vote':
            return t('common:metadata.titles.createVote', { title: electionTitle }) + siteSuffix;
        
        case '/ranked-elections/:id/result':
            return t('common:metadata.titles.result', { title: electionTitle }) + siteSuffix;
        
        case '/ranked-elections/:id/votes':
            return t('common:metadata.titles.votes', { title: electionTitle }) + siteSuffix;
        
        case '/ranked-elections/:id/votes/:voteId':
            return t('common:metadata.titles.voteDetail', { title: electionTitle }) + siteSuffix;
        
        case '/ranked-elections/:electionId/votes/:voteId/update-vote':
            return t('common:metadata.titles.updateVote', { title: electionTitle }) + siteSuffix;
        
        default:
            return t('common:metadata.titles.home');
    }
}

/**
 * Generate internationalized page description based on route and election data
 * @param {string} route - The current route pattern
 * @param {Object} electionData - Election data object with title, id, etc.
 * @param {Function} t - i18n translation function
 * @returns {string} Internationalized page description
 */
export function getPageDescriptionI18n(route, electionData = null, t = null) {
    // Fallback to non-i18n version if no translation function provided
    if (!t) {
        return getPageDescription(route, electionData);
    }

    // Handle loading state
    if (!electionData && needsElectionData(route)) {
        return getLoadingDescriptionI18n(route, t);
    }

    const electionTitle = electionData?.title || t('common:metadata.titles.loading.election');
    const domain = t('common:metadata.domain');

    switch (route) {
        case '/':
            return t('common:metadata.descriptions.home');
        
        case '/create-election':
            return t('common:metadata.descriptions.createElection', { domain });
        
        case '/ranked-elections':
            return t('common:metadata.descriptions.elections', { domain });
        
        case '/ranked-elections/:id':
            return t('common:metadata.descriptions.election', { title: electionTitle, domain });
        
        case '/ranked-elections/:id/create-vote':
            return t('common:metadata.descriptions.createVote', { title: electionTitle, domain });
        
        case '/ranked-elections/:id/result':
            return t('common:metadata.descriptions.result', { title: electionTitle, domain });
        
        case '/ranked-elections/:id/votes':
            return t('common:metadata.descriptions.votes', { title: electionTitle, domain });
        
        case '/ranked-elections/:id/votes/:voteId':
            return t('common:metadata.descriptions.voteDetail', { title: electionTitle, domain });
        
        case '/ranked-elections/:electionId/votes/:voteId/update-vote':
            return t('common:metadata.descriptions.updateVote', { title: electionTitle, domain });
        
        default:
            return t('common:metadata.descriptions.home');
    }
}

/**
 * Generate page title based on route and election data (non-internationalized fallback)
 * @param {string} route - The current route pattern
 * @param {Object} electionData - Election data object with title, id, etc.
 * @param {Object} additionalData - Additional data like vote details
 * @returns {string} Page title
 */
export function getPageTitle(route, electionData = null, additionalData = {}) {
    // Handle loading state
    if (!electionData && needsElectionData(route)) {
        return getLoadingTitle(route);
    }

    // For encrypted elections, use generic titles without the election title
    const isEncrypted = electionData?.isEncrypted || false;
    const electionTitle = electionData?.title || "Election";

    switch (route) {
        case '/':
            return DEFAULT_TITLE;

        case '/create-election':
            return `Create Election${SITE_SUFFIX}`;

        case '/ranked-elections':
            return `Elections${SITE_SUFFIX}`;

        case '/ranked-elections/:id':
            return isEncrypted ? `Ranked Choice Election${SITE_SUFFIX}` : `${electionTitle}${SITE_SUFFIX}`;

        case '/ranked-elections/:id/create-vote':
            return isEncrypted ? `Cast Your Vote${SITE_SUFFIX}` : `Cast Vote: ${electionTitle}${SITE_SUFFIX}`;

        case '/ranked-elections/:id/result':
            return isEncrypted ? `Election Results${SITE_SUFFIX}` : `Results: ${electionTitle}${SITE_SUFFIX}`;

        case '/ranked-elections/:id/votes':
            return isEncrypted ? `Election Votes${SITE_SUFFIX}` : `Votes: ${electionTitle}${SITE_SUFFIX}`;

        case '/ranked-elections/:id/votes/:voteId':
            return isEncrypted ? `Vote Details${SITE_SUFFIX}` : `Vote Detail: ${electionTitle}${SITE_SUFFIX}`;

        case '/ranked-elections/:electionId/votes/:voteId/update-vote':
            return isEncrypted ? `Update Your Vote${SITE_SUFFIX}` : `Update Vote: ${electionTitle}${SITE_SUFFIX}`;

        default:
            return DEFAULT_TITLE;
    }
}

/**
 * Generate page description based on route and election data
 * @param {string} route - The current route pattern
 * @param {Object} electionData - Election data object with title, id, etc.
 * @param {Object} additionalData - Additional data like vote details
 * @returns {string} Page description
 */
export function getPageDescription(route, electionData = null, additionalData = {}) {
    // Handle loading state
    if (!electionData && needsElectionData(route)) {
        return getLoadingDescription(route);
    }

    // For encrypted elections, use generic descriptions without the election title
    const isEncrypted = electionData?.isEncrypted || false;
    const electionTitle = electionData?.title || "Election";

    switch (route) {
        case '/':
            return DEFAULT_DESCRIPTION;

        case '/create-election':
            return `Create a new ranked choice election on ${DOMAIN}`;

        case '/ranked-elections':
            return `Browse ranked choice elections on ${DOMAIN}`;

        case '/ranked-elections/:id':
            return isEncrypted
                ? `View details for a ranked choice election on ${DOMAIN}`
                : `View details for "${electionTitle}" election on ${DOMAIN}`;

        case '/ranked-elections/:id/create-vote':
            return isEncrypted
                ? `Cast your vote in a ranked choice election on ${DOMAIN}`
                : `Cast your vote about "${electionTitle}" in a ranked vote election on ${DOMAIN}`;

        case '/ranked-elections/:id/result':
            return isEncrypted
                ? `View results for a ranked choice election on ${DOMAIN}`
                : `View results for "${electionTitle}" ranked choice election on ${DOMAIN}`;

        case '/ranked-elections/:id/votes':
            return isEncrypted
                ? `View all votes for a ranked choice election on ${DOMAIN}`
                : `View all votes for "${electionTitle}" election on ${DOMAIN}`;

        case '/ranked-elections/:id/votes/:voteId':
            return isEncrypted
                ? `View vote details for a ranked choice election on ${DOMAIN}`
                : `View vote details for "${electionTitle}" election on ${DOMAIN}`;

        case '/ranked-elections/:electionId/votes/:voteId/update-vote':
            return isEncrypted
                ? `Update your vote in a ranked choice election on ${DOMAIN}`
                : `Update your vote for "${electionTitle}" ranked choice election on ${DOMAIN}`;

        default:
            return DEFAULT_DESCRIPTION;
    }
}

/**
 * Check if a route requires election data for meaningful titles/descriptions
 * @param {string} route - The route pattern
 * @returns {boolean} True if election data is needed
 */
function needsElectionData(route) {
    return route.includes('/ranked-elections/') && route !== '/ranked-elections';
}

/**
 * Get internationalized loading state title while election data is being fetched
 * @param {string} route - The route pattern
 * @param {Function} t - i18n translation function
 * @returns {string} Internationalized loading title
 */
function getLoadingTitleI18n(route, t) {
    const siteSuffix = t('common:metadata.siteSuffix');
    
    if (route.includes('/create-vote')) {
        return t('common:metadata.titles.loading.createVote') + siteSuffix;
    } else if (route.includes('/result')) {
        return t('common:metadata.titles.loading.result') + siteSuffix;
    } else if (route.includes('/votes') && route.includes('/update-vote')) {
        return t('common:metadata.titles.loading.updateVote') + siteSuffix;
    } else if (route.includes('/votes/')) {
        return t('common:metadata.titles.loading.voteDetail') + siteSuffix;
    } else if (route.includes('/votes')) {
        return t('common:metadata.titles.loading.votes') + siteSuffix;
    } else {
        return t('common:metadata.titles.loading.election') + siteSuffix;
    }
}

/**
 * Get internationalized loading state description while election data is being fetched
 * @param {string} route - The route pattern
 * @param {Function} t - i18n translation function
 * @returns {string} Internationalized loading description
 */
function getLoadingDescriptionI18n(route, t) {
    const domain = t('common:metadata.domain');
    
    if (route.includes('/create-vote')) {
        return t('common:metadata.descriptions.loading.createVote', { domain });
    } else if (route.includes('/result')) {
        return t('common:metadata.descriptions.loading.result', { domain });
    } else if (route.includes('/votes') && route.includes('/update-vote')) {
        return t('common:metadata.descriptions.loading.updateVote', { domain });
    } else if (route.includes('/votes/')) {
        return t('common:metadata.descriptions.loading.voteDetail', { domain });
    } else if (route.includes('/votes')) {
        return t('common:metadata.descriptions.loading.votes', { domain });
    } else {
        return t('common:metadata.descriptions.loading.election', { domain });
    }
}

/**
 * Get loading state title while election data is being fetched (non-internationalized fallback)
 * @param {string} route - The route pattern
 * @returns {string} Loading title
 */
function getLoadingTitle(route) {
    if (route.includes('/create-vote')) {
        return `Cast Vote${SITE_SUFFIX}`;
    } else if (route.includes('/result')) {
        return `Results${SITE_SUFFIX}`;
    } else if (route.includes('/votes') && route.includes('/update-vote')) {
        return `Update Vote${SITE_SUFFIX}`;
    } else if (route.includes('/votes/')) {
        return `Vote Detail${SITE_SUFFIX}`;
    } else if (route.includes('/votes')) {
        return `Votes${SITE_SUFFIX}`;
    } else {
        return `Election${SITE_SUFFIX}`;
    }
}

/**
 * Get loading state description while election data is being fetched
 * @param {string} route - The route pattern
 * @returns {string} Loading description
 */
function getLoadingDescription(route) {
    if (route.includes('/create-vote')) {
        return `Cast your vote in a ranked choice election on ${DOMAIN}`;
    } else if (route.includes('/result')) {
        return `View results for a ranked choice election on ${DOMAIN}`;
    } else if (route.includes('/votes') && route.includes('/update-vote')) {
        return `Update your vote in a ranked choice election on ${DOMAIN}`;
    } else if (route.includes('/votes/')) {
        return `View vote details for a ranked choice election on ${DOMAIN}`;
    } else if (route.includes('/votes')) {
        return `View all votes for a ranked choice election on ${DOMAIN}`;
    } else {
        return `View details for a ranked choice election on ${DOMAIN}`;
    }
}

/**
 * Determine route pattern from actual URL path
 * @param {string} pathname - The actual URL pathname
 * @returns {string} Route pattern for metadata generation
 */
export function getRoutePattern(pathname) {
    // Handle exact matches first
    if (pathname === '/') return '/';
    if (pathname === '/create-election') return '/create-election';
    if (pathname === '/ranked-elections') return '/ranked-elections';

    // Handle parameterized routes
    if (pathname.match(/^\/ranked-elections\/[^/]+\/create-vote$/)) {
        return '/ranked-elections/:id/create-vote';
    }
    if (pathname.match(/^\/ranked-elections\/[^/]+\/result$/)) {
        return '/ranked-elections/:id/result';
    }
    if (pathname.match(/^\/ranked-elections\/[^/]+\/votes\/[^/]+\/update-vote$/)) {
        return '/ranked-elections/:electionId/votes/:voteId/update-vote';
    }
    if (pathname.match(/^\/ranked-elections\/[^/]+\/votes\/[^/]+$/)) {
        return '/ranked-elections/:id/votes/:voteId';
    }
    if (pathname.match(/^\/ranked-elections\/[^/]+\/votes$/)) {
        return '/ranked-elections/:id/votes';
    }
    if (pathname.match(/^\/ranked-elections\/[^/]+$/)) {
        return '/ranked-elections/:id';
    }

    // Default fallback
    return '/';
}